import { useState } from 'react';
import {
  FloatingNextArrow,
  FloatingPrevArrow,
} from '@/components/CarouselControls';
import { Carousel, CarouselItem } from 'react-aria-carousel';
import type { HeroBannerData, HeroBannerSlide } from '@/types';
import * as Styled from './HeroBanner.styled';

interface HeroBannerProps {
  data: HeroBannerData;
}

const Slide = ({ slide, index }: { slide: HeroBannerSlide; index: number }) => (
  <Styled.Slide>
    <Styled.Copy>
      {slide.eyebrow ? <Styled.Eyebrow>{slide.eyebrow}</Styled.Eyebrow> : null}
      {slide.title ? (
        <Styled.Title>
          {slide.title}
          {slide.titleEmphasis ? (
            <Styled.TitleEmphasis>{slide.titleEmphasis}</Styled.TitleEmphasis>
          ) : null}
        </Styled.Title>
      ) : null}
      {slide.subtitle ? (
        <Styled.Subtitle>{slide.subtitle}</Styled.Subtitle>
      ) : null}
      {slide.ctaText && slide.link ? (
        <Styled.Cta href={slide.link}>{slide.ctaText}</Styled.Cta>
      ) : null}
    </Styled.Copy>

    {slide.videoUrl ? (
      <Styled.SlideVideo
        autoPlay
        loop
        muted
        playsInline
        src={slide.videoUrl}
      />
    ) : (
      <picture>
        {slide.mobileImage ? (
          <source media="(max-width: 47.99rem)" srcSet={slide.mobileImage} />
        ) : null}
        <Styled.SlideImage
          alt={slide.desktopImageAltText}
          loading={index === 0 ? 'eager' : 'lazy'}
          fetchPriority={index === 0 ? 'high' : undefined}
          src={slide.desktopImage}
        />
      </picture>
    )}
  </Styled.Slide>
);

export const HeroBanner = ({ data }: HeroBannerProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const slideCount = data.slides.length;
  const isCarousel = slideCount > 1;
  const activeSlide = data.slides[activeIndex] ?? data.slides[0];

  if (slideCount === 0) return null;

  return (
    <Styled.Wrapper>
      <Carousel
        aria-label="Hero banner"
        autoplay={isCarousel && data.autoplay}
        autoplayInterval={data.autoplayInterval}
        itemsPerPage={1}
        loop="infinite"
        onActivePageIndexChange={({ index }) => setActiveIndex(index)}
      >
        <Styled.Frame>
          <Styled.Background $theme={activeSlide?.backgroundTheme ?? 'poolside'} />
          <Styled.Vignette />

          <Styled.Scroller>
            {data.slides.map((slide, index) => (
              <CarouselItem key={index}>
                <Slide slide={slide} index={index} />
              </CarouselItem>
            ))}
          </Styled.Scroller>

          {isCarousel ? (
            <Styled.ArrowWrapper>
              <FloatingPrevArrow />
              <FloatingNextArrow />
            </Styled.ArrowWrapper>
          ) : null}

          {isCarousel ? (
            <Styled.DotsPill>
              {({ index }) => <Styled.Dot index={index} />}
            </Styled.DotsPill>
          ) : null}
        </Styled.Frame>
      </Carousel>
    </Styled.Wrapper>
  );
};
